import { FC, Fragment } from "react";
import { observer } from "mobx-react";
import { DividendForm } from "./dividendDomain";
import { DateQuestion, NumberField, YesNoQuestion } from "../common/commonComponents";
import { Caption } from "../../components-v2/Caption";
import { Alert } from "../../components-v2/Alert";
import { Checkbox } from "../../components-v2/Checkbox";
import { Grid, VBox } from "../../components-v2/Box";
import { LinkButton } from "../../components-v2/Button";
import { CURRENCY_NOK } from "../../utils/currency";

import { useTranslation } from "../../stores/RootStoreContext";
import { RadioFieldQuestion, SelectField } from "../../common/commonComponents";
import { shareTypeLabel } from "../../common/share";

export const DividendView: FC<{
  form: DividendForm;
}> = observer(({ form }) => {
  const { t } = useTranslation();
  return (
    <VBox gap={"md"}>
      <YesNoQuestion
        question={t("decisions.dividend.extraordinaryDividend.questionText")}
        field={form.isExtraordinaryDividend}
        isLocked={form.isProjectLocked}
      />
      {form.isExtraordinaryDividend.value && (
        <>
          <DateQuestion
            question={t("decisions.dividend.extraordinaryDividendDate.title")}
            field={form.extraordinaryDividendDate}
            isLocked={form.isProjectLocked}
          />
          <Caption>
            <p>{t("decisions.dividend.extraordinaryDividendDate.helperText1")}</p>
            <p>{t("decisions.dividend.extraordinaryDividendDate.helperText2")}</p>
          </Caption>
          <VBox>
            <RadioFieldQuestion
              question={{ key: "decisions.dividend.balanceSheet.gmApproval" }}
              field={form.isBalanceSheetApproved}
              fieldName={"isBalanceSheetApproved"}
              isLocked={form.isProjectLocked}
            />
          </VBox>
          {form.isBalanceSheetApproved.value && (
            <DateQuestion
              question={t("decisions.dividend.balanceSheet.gmApproval.date")}
              field={form.extraordinaryDividendGmApprovalDate}
              isLocked={form.isProjectLocked}
            />
          )}
          <YesNoQuestion
            question={t("decisions.dividend.extraordinaryDividendRevised.title")}
            field={form.extraordinaryDividendRevised}
            isLocked={form.isProjectLocked}
          />
          {form.extraordinaryDividendRevised.value === false && (
            <Caption>{t("decisions.dividend.extraordinaryDividendRevised.helperText")}</Caption>
          )}
        </>
      )}
      <YesNoQuestion
        question={t("decisions.dividend.usePowerOfAttorney")}
        field={form.usePowerOfAttorney}
        onChange={(e) => form.setUsePowerOfAttorney(e)}
        isLocked={form.isProjectLocked}
      />
      {form.showPowerOfAttorney && (
        <DateQuestion
          question={t("decisions.increaseShareCapitalAgainstCash.poaDate")}
          field={form.powerOfAttorneyDate}
          isLocked={form.isProjectLocked}
        />
      )}
      <YesNoQuestion
        question={t("decisions.dividend.isDividendEvenlyDivided")}
        field={form.isDividendEvenlyDivided}
        isLocked={form.isProjectLocked}
      />
      {form.isDividendEvenlyDivided.value && (
        <VBox>
          <div>{t("decisions.dividend.dividendEvenlyDivided.title")}</div>
          {form.shareTypes.map((shareType) => {
            const isChecked = form.hasEvenDividend(shareType.sharetypeId);
            return (
              <Fragment key={shareType.sharetypeId}>
                <div>
                  <Checkbox
                    label={t(shareTypeLabel(shareType))}
                    onChange={() => form.toggleEvenDividend(shareType.sharetypeId)}
                    checked={isChecked}
                    disabled={form.isProjectLocked}
                  />
                </div>
                {isChecked && (
                  <Grid cols={2} alignItems={"start"} gap={"md"}>
                    <SelectField
                      label={t("decisions.dividend.evenDividendType.paymentMethod")}
                      fullWidth
                      field={form.getEvenDividendModel(shareType.sharetypeId)}
                      name={shareType.sharetypeCode + "_evenDividendType"}
                      onChange={(value) => {
                        form.toggleEvenDividendType(shareType.sharetypeId, value);
                      }}
                      isLocked={form.isProjectLocked}
                    />
                    <NumberField
                      field={form.amountForShareType(shareType.sharetypeId)}
                      label={
                        form.evenDividendIsValueTotal(shareType.sharetypeId)
                          ? t("decisions.dividend.total-amount")
                          : t("decisions.dividend.amount-per-share")
                      }
                      prefix={CURRENCY_NOK}
                      isLocked={form.isProjectLocked}
                    />
                  </Grid>
                )}
              </Fragment>
            );
          })}
          {!form.validEvenDividendShareTypes && (
            <VBox>
              <Alert
                type="error"
                message={t("decisions.dividend.isDividendEvenlyDivided.missingShareType")}
              />
            </VBox>
          )}
          <RadioFieldQuestion
            question={{ key: "decisions.dividend.dividendPeriodNormal.questionText" }}
            field={form.isDividendPeriodNormal}
            fieldName={"isDividendPeriodNormal"}
            isLocked={form.isProjectLocked}
          />
          {!form.isDividendPeriodNormal.value && (
            <DateQuestion
              question={t("decisions.enter-date")}
              field={form.dividendDate}
              isLocked={form.isProjectLocked}
            />
          )}
        </VBox>
      )}
      {!form.isDividendEvenlyDivided.value && (
        <VBox>
          <div>
            <div>{t("decisions.dividend.dividendNotEvenlyDivided.title")}</div>
            <LinkButton
              label={t("select-all")}
              size={"sm"}
              onClick={form.selectAllUnevenDividendsChecked}
            />
          </div>
          {form.unevenDividendInputs.map(
            ({ shareTypes, shareholderId, shareholderLabel, checked, disabled, onChange }) => (
              <VBox key={shareholderId}>
                <Checkbox
                  label={shareholderLabel}
                  checked={checked}
                  disabled={disabled}
                  onChange={onChange}
                />
                {shareTypes.map(
                  ({ dividend, sharetypeId, sharetypeLabel, checked, disabled, onChange }) => (
                    <VBox key={sharetypeId} marginLeft={"md"}>
                      <Checkbox
                        label={t(sharetypeLabel)}
                        checked={checked}
                        disabled={disabled}
                        onChange={onChange}
                      />
                      {dividend && (
                        <NumberField
                          field={dividend.inputModel}
                          label={t(dividend.label)}
                          prefix={dividend.prefix}
                          isLocked={dividend.isLocked}
                          inline
                        />
                      )}
                    </VBox>
                  ),
                )}
              </VBox>
            ),
          )}
        </VBox>
      )}
      {!form.validUnevenShareholdersSelected && (
        <Alert type="error" message={t("decisions.dividend.shareholder.missing")} />
      )}
      <YesNoQuestion
        question={t("decisions.dividend.dividendRepaymentOfCapital")}
        field={form.dividendRepaymentOfCapital}
        isLocked={form.isProjectLocked}
      />
    </VBox>
  );
});
